import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  Brain,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  FileText,
  Filter,
  Info,
  Search,
  Target
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../redux/store';
import { DBDocumentThunks, DBEntryThunks } from '../../redux/thunks/DBServiceThunks';
import { findSnippetInText } from '../../utils/textMatching';
import DocumentContent from './timeline/document-content';

export type RelevanceLevel = 'highly_relevant' | 'moderately_relevant' | 'slightly_relevant' | 'not_relevant';

interface SmartSearchViewerProps {
  open: boolean;
  onClose: () => void;
  searchResults?: any[];
  query?: string;
  title?: string;
}

export default function SmartSearchViewer({ open, onClose, searchResults = [], query, title }: SmartSearchViewerProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Filters
  const [relevanceFilter, setRelevanceFilter] = useState<RelevanceLevel | 'all'>('all');

  // Filtered results
  const filteredResults = useMemo(() => {
    if (relevanceFilter === 'all') return searchResults;
    return searchResults.filter(r => r.relevance === relevanceFilter);
  }, [searchResults, relevanceFilter]);

  // Group results by documentId
  const resultsByDocument = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    filteredResults.forEach(result => {
      const docId = result.documentId;
      if (!grouped[docId]) {
        grouped[docId] = [];
      }
      grouped[docId].push(result);
    });
    return grouped;
  }, [filteredResults]);

  const documentIds = Object.keys(resultsByDocument);
  const [activeDocumentId, setActiveDocumentId] = useState<string>('');
  const [activeHighlightIndex, setActiveHighlightIndex] = useState(0);
  const [scrollTrigger, setScrollTrigger] = useState(0);
  const [ghostHighlightsCount, setGhostHighlightsCount] = useState<Record<string, number>>({});

  const [documentData, setDocumentData] = useState<Record<string, { text: string; name: string; offsets: Record<string, number> }>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Set initial active document
  useEffect(() => {
    if (documentIds.length > 0 && !activeDocumentId) {
      setActiveDocumentId(documentIds[0]);
    } else if (documentIds.length === 0) {
      setActiveDocumentId('');
    }
  }, [documentIds]);

  // Reset highlight index when changing document
  useEffect(() => {
    setActiveHighlightIndex(0);
  }, [activeDocumentId]);

  // Load document content
  useEffect(() => {
    if (!open || !activeDocumentId || documentData[activeDocumentId]) return;

    const loadDocument = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const documentResult = await dispatch(DBDocumentThunks.find({ _id: activeDocumentId })).unwrap();
        const documents = Array.isArray(documentResult) ? documentResult : documentResult.data || [];
        const document = documents[0];
        const docName = document?.name || t('common.timeline.documentViewer.untitledDocument');

        const entries = await dispatch(DBEntryThunks.find({
          documentId: activeDocumentId,
          $limit: 1000
        })).unwrap();

        const entryArray = Array.isArray(entries) ? entries : entries.data || [];
        const sortedEntries = [...entryArray].sort((a: any, b: any) => (a.metadata?.chunkIndex || 0) - (b.metadata?.chunkIndex || 0));

        const offsets: Record<string, number> = {};
        let currentOffset = 0;

        const fullText = sortedEntries.map((entry: any) => {
          offsets[entry._id] = currentOffset;
          const content = entry.content;
          currentOffset += content.length + 2; // +2 for \n\n
          return content;
        }).join('\n\n');

        setDocumentData(prev => ({
          ...prev,
          [activeDocumentId]: { text: fullText, name: docName, offsets }
        }));
        setIsLoading(false);
      } catch (err: any) {
        console.error('SmartSearchViewer: Error loading document:', err);
        setError(err.message || t('common.timeline.documentViewer.errorLoading'));
        setIsLoading(false);
      }
    };

    loadDocument();
  }, [open, activeDocumentId, dispatch, t]);

  const highlights = useMemo(() => {
    if (!activeDocumentId || !documentData[activeDocumentId]) return [];

    const results = resultsByDocument[activeDocumentId] || [];
    const { text } = documentData[activeDocumentId];

    // Track how many times we've seen each snippet text within each entry
    const entrySnippetOccurrences: Record<string, Record<string, number>> = {};
    let foundCount = 0;
    const totalCount = results.length;

    const allHighlights = results.map((r) => {
      const snippet = r.snippet || r.sourceText || '';

      if (!entrySnippetOccurrences[r._id]) {
        entrySnippetOccurrences[r._id] = {};
      }
      const occurrenceIndex = entrySnippetOccurrences[r._id][snippet] || 0;
      entrySnippetOccurrences[r._id][snippet] = occurrenceIndex + 1;

      const entryOffset = documentData[activeDocumentId].offsets?.[r._id] || 0;
      const match = findSnippetInText(text, snippet, entryOffset);

      if (match) {
        foundCount++;
        return {
          text: snippet,
          absoluteStart: match.start,
          absoluteEnd: match.end,
          reasoning: r.reasoning,
          relevance: r.relevance,
          scrollTrigger
        };
      }

      return null;
    }).filter(Boolean) as any[];

    // Update ghost count
    const ghosts = totalCount - foundCount;
    if (ghosts !== (ghostHighlightsCount[activeDocumentId] || 0)) {
      setGhostHighlightsCount(prev => ({ ...prev, [activeDocumentId]: ghosts }));
    }

    // Sort highlights by absolute position (top-to-bottom)
    const sortedHighlights = [...allHighlights].sort((a, b) => a.absoluteStart - b.absoluteStart);

    // Map focused state
    return sortedHighlights.map((h, idx) => ({
      ...h,
      isFocused: idx === activeHighlightIndex
    }));
  }, [activeDocumentId, documentData, resultsByDocument, activeHighlightIndex, scrollTrigger, ghostHighlightsCount]);

  const currentHighlight = highlights[activeHighlightIndex];

  const handleNextHighlight = () => {
    if (activeHighlightIndex < highlights.length - 1) {
      setActiveHighlightIndex(prev => prev + 1);
    } else if (highlights.length > 0) {
      setScrollTrigger(prev => prev + 1);
    }
  };

  const handlePrevHighlight = () => {
    if (activeHighlightIndex > 0) {
      setActiveHighlightIndex(prev => prev - 1);
    } else if (highlights.length > 0) {
      setScrollTrigger(prev => prev + 1);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-7xl h-[90vh] flex flex-col p-0 overflow-hidden border-none shadow-2xl rounded-2xl">
        <div className="bg-gradient-to-r from-primary via-primary/95 to-primary/90 p-5 text-primary-foreground flex justify-between items-center shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-2.5 bg-gold/20 rounded-xl border border-gold/30">
              <Search className="h-5 w-5 text-gold" />
            </div>
            <div>
              <DialogTitle className="text-xl font-bold tracking-tight">
                {title || t('common.knowledgebase.smartSearch')}
              </DialogTitle>
              <DialogDescription className="text-primary-foreground/60 font-medium">
                {query ? t('common.knowledgebase.searchResultsFor', { query }) : t('common.knowledgebase.exploringResults')}
              </DialogDescription>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-lg border border-white/10">
              <Filter className="h-4 w-4 text-primary-foreground/50" />
              <select
                value={relevanceFilter}
                onChange={(e) => setRelevanceFilter(e.target.value as any)}
                className="bg-transparent text-sm font-semibold outline-none text-primary-foreground cursor-pointer"
              >
                <option value="all" className="bg-slate-800 italic">{t('common.knowledgebase.allRelevance')}</option>
                <option value="highly_relevant" className="bg-slate-800 text-green-400">{t('common.knowledgebase.highlyRelevant')}</option>
                <option value="moderately_relevant" className="bg-slate-800 text-yellow-400">{t('common.knowledgebase.moderatelyRelevant')}</option>
                <option value="slightly_relevant" className="bg-slate-800 text-slate-400">{t('common.knowledgebase.slightlyRelevant')}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden bg-background">
          {/* Sidebar - Document List */}
          <div className="w-72 bg-card border-e flex flex-col shadow-sm">
            <div className="p-4 border-b bg-card/50">
              <h3 className="text-xs font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <FileText className="h-3.5 w-3.5" />
                {t('common.knowledgebase.relevantDocuments')}
              </h3>
            </div>
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-1">
                {documentIds.map(id => (
                  <button
                    key={id}
                    onClick={() => setActiveDocumentId(id)}
                    className={cn(
                      "w-full flex items-start gap-3 p-3 rounded-xl transition-all duration-200 text-left group",
                      activeDocumentId === id
                        ? "bg-gold/5 border-gold/10 shadow-sm"
                        : "hover:bg-accent/50 border-transparent hover:border-border"
                    )}
                  >
                    <div className={cn(
                      "p-2 rounded-lg shrink-0",
                      activeDocumentId === id ? "bg-gold text-white shadow-gold/10 shadow-lg" : "bg-secondary text-muted-foreground group-hover:bg-secondary/70"
                    )}>
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <p className={cn(
                        "text-sm font-bold truncate mb-0.5",
                        activeDocumentId === id ? "text-gold" : "text-foreground/80"
                      )}>
                        {resultsByDocument[id][0]?.documentName || t('common.labels.document')}
                      </p>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={cn(
                          "px-1.5 py-0 h-4 text-[10px] font-bold rounded-full",
                          activeDocumentId === id ? "bg-gold/20 text-gold" : "bg-secondary text-muted-foreground"
                        )}>
                          {t('common.knowledgebase.matchesCount', { count: resultsByDocument[id].length - (ghostHighlightsCount[id] || 0) })}
                        </Badge>
                      </div>
                    </div>
                  </button>
                ))}
                {documentIds.length === 0 && (
                  <div className="p-8 text-center">
                    <p className="text-sm text-muted-foreground italic">{t('common.knowledgebase.noResultsForFilter')}</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden relative">
            {/* Highlight Navigation Bar */}
            {activeDocumentId && resultsByDocument[activeDocumentId]?.length > 0 && (
              <div className="px-6 py-3 bg-card border-b flex items-center justify-between z-10 shadow-sm">
                <div className="flex items-center gap-4">
                  <span className="text-xs font-black text-muted-foreground uppercase tracking-widest flex items-center gap-2">
                    <Target className="h-3.5 w-3.5" />
                    {t('common.knowledgebase.viewingResult')}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 rounded-lg"
                      disabled={highlights.length === 0}
                      onClick={handlePrevHighlight}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-bold tabular-nums bg-secondary px-3 py-1 rounded-md border border-border text-foreground/80">
                      {highlights.length > 0 ? activeHighlightIndex + 1 : 0} / {highlights.length}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 rounded-lg"
                      disabled={highlights.length === 0}
                      onClick={handleNextHighlight}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {currentHighlight?.relevance && (
                    <Badge className={cn(
                      "px-3 py-1 rounded-full uppercase text-[10px] font-black tracking-tighter border-none",
                      currentHighlight.relevance === 'highly_relevant' ? "bg-green-100 text-green-700" :
                        currentHighlight.relevance === 'moderately_relevant' ? "bg-yellow-100 text-yellow-700" :
                          "bg-secondary text-muted-foreground"
                    )}>
                      {currentHighlight.relevance.replace('_', ' ')}
                    </Badge>
                  )}
                  <div className="flex items-center gap-1.5 px-2 py-1 bg-gold/5 text-gold rounded-lg border border-gold/10">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    <span className="text-[10px] font-bold uppercase tracking-wide">{t('common.knowledgebase.verifiedByAI')}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex-1 flex flex-col p-6 overflow-hidden">
              <div className="flex-1 overflow-y-auto bg-card rounded-2xl shadow-inner border border-border p-8 md:p-12 relative scroll-smooth">
                {isLoading && (
                  <div className="space-y-6">
                    <Skeleton className="h-8 w-1/3 rounded-lg" />
                    <div className="space-y-3">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-5/6" />
                    </div>
                    <Skeleton className="h-32 w-full rounded-2xl" />
                    <div className="space-y-3">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>
                )}

                {error && (
                  <div className="flex flex-col items-center justify-center py-20 text-center">
                    <div className="p-5 bg-red-50 rounded-2xl mb-4 border border-red-100">
                      <Info className="h-10 w-10 text-red-500" />
                    </div>
                    <p className="text-xl font-black text-foreground mb-2">{t('common.labels.error')}</p>
                    <p className="text-muted-foreground max-w-sm font-medium">{error}</p>
                  </div>
                )}

                {!isLoading && !error && activeDocumentId && documentData[activeDocumentId] && (
                  <DocumentContent
                    documentText={documentData[activeDocumentId].text}
                    highlights={highlights}
                  />
                )}

                {!isLoading && !activeDocumentId && !error && (
                  <div className="flex flex-col items-center justify-center h-full text-center p-12">
                    <div className="p-6 bg-secondary rounded-full mb-6">
                      <Search className="h-12 w-12 text-muted-foreground/40" />
                    </div>
                    <h3 className="text-xl font-bold text-muted-foreground mb-2">{t('common.knowledgebase.noResultsSelected')}</h3>
                    <p className="text-muted-foreground max-w-xs">{t('common.knowledgebase.selectDocToSeeMatches')}</p>
                  </div>
                )}
              </div>

              {/* Enhanced AI Reasoning Card */}
              {!isLoading && currentHighlight && (
                <div className="mt-6 p-5 bg-card border border-border rounded-2xl flex gap-5 items-start shadow-xl animate-in slide-in-from-bottom-4 duration-500">
                  <div className="p-3 bg-gold rounded-2xl shadow-lg shadow-gold/20 shrink-0">
                    <Brain className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-xs font-black text-gold uppercase tracking-widest">
                        {t('common.knowledgebase.aiInsight')}
                      </h4>
                      <Separator orientation="vertical" className="h-3 bg-border" />
                      <span className="text-[10px] font-bold text-muted-foreground flex items-center gap-1">
                        {t('common.knowledgebase.matchStrength')}
                        <span className="text-gold">{currentHighlight.relevance === 'highly_relevant' ? t('common.knowledgebase.strength.strong') : t('common.knowledgebase.strength.medium')}</span>
                      </span>
                    </div>
                    <p className="text-sm text-foreground/80 leading-relaxed font-semibold italic border-s-4 border-gold/20 ps-4 py-1">
                      "{currentHighlight.reasoning}"
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
